import { BLACK, WHITE, colorName } from '../primitives/color.js'
import {
  EMPTY,
  KING,
  codeColor,
  codeType,
  pieceFromLetter,
  pieceLetter,
  type PieceCode,
} from '../primitives/piece.js'
import { parseSquare, square, squareName, type Square } from '../primitives/square.js'
import { err, ok, type Result } from '../result.js'
import {
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  emptyPosition,
  type CastlingRights,
  type Position,
} from './position.js'

export const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'

export type FenError =
  | { readonly kind: 'wrongFieldCount'; readonly count: number }
  | { readonly kind: 'wrongRankCount'; readonly count: number }
  | { readonly kind: 'rankWrongWidth'; readonly rank: number; readonly width: number }
  | { readonly kind: 'badPieceLetter'; readonly letter: string }
  | { readonly kind: 'badTurn'; readonly value: string }
  | { readonly kind: 'badCastling'; readonly value: string }
  | { readonly kind: 'badEnPassant'; readonly value: string }
  | { readonly kind: 'badCounter'; readonly field: 'halfmoves' | 'fullmoves'; readonly value: string }
  | { readonly kind: 'kingCount'; readonly color: 'white' | 'black'; readonly count: number }

export const describeFenError = (error: FenError): string => {
  switch (error.kind) {
    case 'wrongFieldCount':
      return `FEN needs 4 to 6 space-separated fields, got ${error.count}`
    case 'wrongRankCount':
      return `FEN board needs 8 ranks, got ${error.count}`
    case 'rankWrongWidth':
      return `rank ${8 - error.rank} describes ${error.width} squares, expected 8`
    case 'badPieceLetter':
      return `unknown piece letter ${JSON.stringify(error.letter)}`
    case 'badTurn':
      return `side to move must be "w" or "b", got ${JSON.stringify(error.value)}`
    case 'badCastling':
      return `unparseable castling field ${JSON.stringify(error.value)}`
    case 'badEnPassant':
      return `unparseable en passant square ${JSON.stringify(error.value)}`
    case 'badCounter':
      return `${error.field} must be a non-negative integer, got ${JSON.stringify(error.value)}`
    case 'kingCount':
      return `expected exactly one ${error.color} king, found ${error.count}`
  }
}

const parseCastling = (field: string): CastlingRights | null => {
  if (field === '-') return 0
  let rights = 0
  for (const char of field) {
    switch (char) {
      case 'K':
        rights |= CASTLE_WK
        break
      case 'Q':
        rights |= CASTLE_WQ
        break
      case 'k':
        rights |= CASTLE_BK
        break
      case 'q':
        rights |= CASTLE_BQ
        break
      default:
        return null
    }
  }
  return rights
}

const parseCounter = (field: string): number | null => {
  if (!/^\d+$/.test(field)) return null
  return Number.parseInt(field, 10)
}

export const parseFen = (fen: string): Result<Position, FenError> => {
  const fields = fen.trim().split(/\s+/)
  if (fields.length < 4 || fields.length > 6) return err({ kind: 'wrongFieldCount', count: fields.length })

  const [boardField, turnField, castlingField, epField, halfmoveField, fullmoveField] = fields as [
    string,
    string,
    string,
    string,
    string | undefined,
    string | undefined,
  ]

  const ranks = boardField.split('/')
  if (ranks.length !== 8) return err({ kind: 'wrongRankCount', count: ranks.length })

  const pos = emptyPosition()
  const kingSquares: [Square[], Square[]] = [[], []]

  for (let rankIndex = 0; rankIndex < 8; rankIndex += 1) {
    // FEN lists rank 8 first; our rank 0 is rank 1.
    const rank = 7 - rankIndex
    let file = 0
    for (const char of ranks[rankIndex] as string) {
      if (char >= '1' && char <= '8') {
        file += char.charCodeAt(0) - 48
        continue
      }
      const code = pieceFromLetter(char)
      if (code === null) return err({ kind: 'badPieceLetter', letter: char })
      if (file > 7) return err({ kind: 'rankWrongWidth', rank: rankIndex, width: file + 1 })
      const target = square(file, rank)
      pos.board[target] = code
      if (codeType(code) === KING) kingSquares[codeColor(code)].push(target)
      file += 1
    }
    if (file !== 8) return err({ kind: 'rankWrongWidth', rank: rankIndex, width: file })
  }

  for (const color of [WHITE, BLACK] as const) {
    const found = kingSquares[color]
    if (found.length !== 1) {
      return err({ kind: 'kingCount', color: colorName(color), count: found.length })
    }
    pos.kings[color] = found[0] as Square
  }

  if (turnField !== 'w' && turnField !== 'b') return err({ kind: 'badTurn', value: turnField })
  pos.turn = turnField === 'w' ? WHITE : BLACK

  const castling = parseCastling(castlingField)
  if (castling === null) return err({ kind: 'badCastling', value: castlingField })
  pos.castling = castling

  if (epField === '-') {
    pos.epSquare = null
  } else {
    const epSquare = parseSquare(epField)
    if (epSquare === null) return err({ kind: 'badEnPassant', value: epField })
    pos.epSquare = epSquare
  }

  // The last two fields are optional: EPD and many published test positions
  // stop after the en passant square.
  if (halfmoveField !== undefined) {
    const halfmoves = parseCounter(halfmoveField)
    if (halfmoves === null) return err({ kind: 'badCounter', field: 'halfmoves', value: halfmoveField })
    pos.halfmoves = halfmoves
  }
  if (fullmoveField !== undefined) {
    const fullmoves = parseCounter(fullmoveField)
    if (fullmoves === null) return err({ kind: 'badCounter', field: 'fullmoves', value: fullmoveField })
    pos.fullmoves = fullmoves
  }

  return ok(pos)
}

const castlingToString = (rights: CastlingRights): string => {
  if (rights === 0) return '-'
  return (
    (rights & CASTLE_WK ? 'K' : '') +
    (rights & CASTLE_WQ ? 'Q' : '') +
    (rights & CASTLE_BK ? 'k' : '') +
    (rights & CASTLE_BQ ? 'q' : '')
  )
}

/** Just the board field — the part EPD and position keys share with FEN. */
export const boardToFen = (pos: Position): string => {
  const ranks: string[] = []
  for (let rank = 7; rank >= 0; rank -= 1) {
    let line = ''
    let gap = 0
    for (let file = 0; file < 8; file += 1) {
      const code = pos.board[square(file, rank)] as PieceCode
      if (code === EMPTY) {
        gap += 1
        continue
      }
      if (gap > 0) {
        line += String(gap)
        gap = 0
      }
      line += pieceLetter(code)
    }
    if (gap > 0) line += String(gap)
    ranks.push(line)
  }
  return ranks.join('/')
}

export const toFen = (pos: Position): string =>
  [
    boardToFen(pos),
    pos.turn === WHITE ? 'w' : 'b',
    castlingToString(pos.castling),
    pos.epSquare === null ? '-' : squareName(pos.epSquare),
    String(pos.halfmoves),
    String(pos.fullmoves),
  ].join(' ')

export const initialPosition = (): Position => {
  const parsed = parseFen(INITIAL_FEN)
  /* c8 ignore next */
  if (!parsed.ok) throw new Error('INITIAL_FEN is not parseable — this is a bug')
  return parsed.value
}
